import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import dynamic from 'next/dynamic';
import { useBridgeState } from '@digitaz/bridge-sdk-client';
import { useQuery } from '@apollo/client';
import { useRouter } from 'next/router';
import { Dispatch } from 'redux';
import {
  setCurrentItem,
  setError,
  setProfilePromotionItemId,
  togglePromotionModalView
} from '../../../actions';
import useWebViewPaymentStatusPostMessageHandler from '../../../hooks/useWebViewPaymentStatusPostMessageHandler';
import { WhiteHeader } from '../../WhiteHeader';
import { getItemTitle } from '../../../utils/helpers/getCurrentItemDescriptionTitle';
import {
  selectFailureWebViewPaymentStatusModalViewState,
  selectRestoreAlertModalState,
  selectSuccessPromotionModalViewState,
  selectSuccessWebviewPaymentStatusModalViewState
} from '../../../store/selectors';
import { useTogglePromotionModalView } from '../../../hooks/useTogglePromotionModalView';
import { useCurrentItemData } from '../../../hooks/useCurrentItemData';
import { CurrentItemSections } from '../../Ad/Sections/CurrentItemSections';
import { FullItemData } from '../../Ad/Sections/CurrentItemSections/types';
import { CurrentItemPageSchema } from './CurrentItemPageSchema';
import useScreenSize from '../../../hooks/useScreenSize';
import { CurrentItemSectionWrapper, HeaderWrapper } from './styled';
import { SimilarAdsBlock } from '../../Ad/Sections/blocks/SimilarAdsBlock';
import { CurrentItemLayout } from '../../Ad/CurrentItemLayout';
import {
  CurrentItemQuery,
  ItemUpdateStatusEnum,
  UserRelatedItemQuery
} from '../../../graphql/graphql';
import { CurrentItemPlaceholder } from '../../Placeholders/CurrentItemPlaceholder';
import { useClient } from '../../../hooks/useClient';
import { getCurrentItem, getUserRelatedItem } from '../../../api/queries';
import { CurrentItemData } from '../../../utils/helpers/item';
import { useItemRevalidation } from '../../../hooks/useItemRevalidation';
import { cleanHtmlExtension } from '../../../utils/helpers/middleware';

const mapDispatchToProps = (dispatch: Dispatch) => ({
  setErrorUI: (errorState: boolean) => dispatch(setError(errorState))
});

const CurrentItemAlerts = dynamic(
  () => import('./CurrentItemAlerts').then(mod => mod.CurrentItemAlerts),
  {
    ssr: false
  }
);

const ValidatedSection = dynamic(
  () =>
    import('../../Ad/Sections/ValidatedSection').then(
      mod => mod.ValidatedSection
    ),
  {
    ssr: false
  }
);

const mergeUserRelatedItemData = (
  baseItemData: NonNullable<FullItemData>,
  userRelatedItemData?: UserRelatedItemQuery['item'] | null
): FullItemData => {
  if (!userRelatedItemData) return baseItemData;

  const { business: userRelatedBusiness, ...userRelatedFields } =
    userRelatedItemData || {};
  const mergedItemData = {
    ...baseItemData,
    ...userRelatedFields
  };

  if (!baseItemData.business) return mergedItemData;

  return {
    ...mergedItemData,
    business: {
      ...baseItemData.business,
      ...userRelatedBusiness
    }
  };
};

const CurrentItemPageComponent: React.FC<{
  setErrorUI: (value: boolean) => void;
  currentItemData: CurrentItemData;
  error: unknown | null;
  itemId: string;
  ssrVersion: string;
}> = ({ setErrorUI, currentItemData, error, itemId, ssrVersion }) => {
  const dispatch = useDispatch();
  const router = useRouter();
  const { alert } = router.query;
  const alertParam = Array.isArray(alert) ? alert[0] : alert;
  const asPath = cleanHtmlExtension(router.asPath);
  const [isFavorited, setFavorited] = useState(false);
  const [isShowForbiddenAlert, setIsShowForbiddenAlert] = useState(false);
  const [showReloadPageAlert, setShowReloadPageAlert] = useState(false);
  const { isWebView } = useBridgeState();
  const { isSmall } = useScreenSize();
  const isClient = useClient();

  const {
    data: nonSsrCurrentItemData,
    loading: currentItemDataLoading,
    refetch: nonSsrCurrentItemRefetch
  } = useQuery<CurrentItemQuery>(getCurrentItem, {
    variables: { id: itemId },
    ssr: false,
    fetchPolicy: 'network-only',
    skip: !!currentItemData
  });

  const {
    data: userRelatedItemQueryResponseData,
    loading: userRelatedItemLoading
  } = useQuery<UserRelatedItemQuery>(getUserRelatedItem, {
    variables: {
      id: itemId
    },
    ssr: false,
    skip: !currentItemData,
    fetchPolicy: 'cache-and-network'
  });

  const { isDataRefreshing, invalidateItemData } = useItemRevalidation(
    itemId,
    userRelatedItemQueryResponseData?.item?.updatedAt,
    ssrVersion,
    nonSsrCurrentItemData?.item?.state,
    nonSsrCurrentItemData?.item?.updatedAt,
    nonSsrCurrentItemRefetch
  );

  const fullItemData = useMemo<FullItemData>(() => {
    const userRelatedItemData = userRelatedItemQueryResponseData?.item;

    if (invalidateItemData?.item) {
      return mergeUserRelatedItemData(
        invalidateItemData.item,
        userRelatedItemData
      );
    }

    if (
      userRelatedItemQueryResponseData &&
      !userRelatedItemQueryResponseData?.item
    )
      return null;

    if (currentItemData) {
      return mergeUserRelatedItemData(currentItemData, userRelatedItemData);
    }

    if (nonSsrCurrentItemData?.item) return nonSsrCurrentItemData.item;

    return null;
  }, [
    currentItemData,
    userRelatedItemQueryResponseData,
    nonSsrCurrentItemData,
    invalidateItemData
  ]);

  useEffect(() => {
    if (fullItemData) {
      dispatch(setCurrentItem(fullItemData));
    }
  }, [dispatch, fullItemData]);

  const { isVisible: isOpenRestoreAlertModal } = useSelector(
    selectRestoreAlertModalState
  );
  const { isVisible: isOpenSuccessAlertModal } = useSelector(
    selectSuccessPromotionModalViewState
  );
  const { isVisible: isOpenWebViewSuccessAlertModal } = useSelector(
    selectSuccessWebviewPaymentStatusModalViewState
  );
  const { isVisible: isOpenWebViewFailureAlertModal } = useSelector(
    selectFailureWebViewPaymentStatusModalViewState
  );

  const shouldLoadAlerts =
    isOpenRestoreAlertModal ||
    isOpenSuccessAlertModal ||
    isShowForbiddenAlert ||
    isOpenWebViewSuccessAlertModal ||
    isOpenWebViewFailureAlertModal ||
    showReloadPageAlert;

  const {
    item,
    maskedPhones,
    isEditAvailable,
    isRemoveAvailable,
    isAuthor,
    isResidence,
    isAgency,
    isPublished,
    isLimited,
    isExpired,
    isRejected,
    isPending,
    isExpiredManually,
    paidServices,
    itemPath,
    prettyPrice,
    prettyArea,
    prettyAreaWithUnits,
    prettyLandAreaWithUnits,
    showElectronicBanners,
    getPriceCurrency,
    isValidated,
    isLeased
  } = useCurrentItemData(fullItemData, asPath);

  const {
    onOpenRestoreViewHandler,
    onOpenLimitPaymentViewHandler,
    onAcceptRestorePaymentHandleClick,
    onDeclineRestorePayment
  } = useTogglePromotionModalView(item || {}, setShowReloadPageAlert);

  const itemTitleData = {
    lease: item?.isLeased,
    area: prettyArea,
    rooms: item?.rooms,
    areaCurrency: item?.area?.units,
    category: item?.category?.name,
    location: item?.location
  };

  const calcModalDescription = getItemTitle({
    ...itemTitleData,
    isClearString: true,
    simpleData: true
  });

  useEffect(() => {
    if (error) setErrorUI(true);
  }, [error, setErrorUI]);

  useEffect(() => {
    if (!router.isReady) return;
    if (alertParam === ItemUpdateStatusEnum.Forbidden.toLowerCase()) {
      setIsShowForbiddenAlert(true);
    }
  }, [router.isReady, alertParam]);

  useWebViewPaymentStatusPostMessageHandler(
    useCallback(() => {
      dispatch(togglePromotionModalView(false));
      dispatch(setProfilePromotionItemId(null));

      if (router.asPath.includes('#')) {
        router.replace(router.asPath.split('#')[0], undefined, {
          shallow: true,
          scroll: false
        });
      }
    }, [dispatch, router])
  );

  const handleCloseForbiddenPopup = () => {
    setIsShowForbiddenAlert(false);

    const { alert: _, ...rest } = router.query;
    router.replace({ pathname: router.pathname, query: rest }, undefined, {
      shallow: true
    });
  };

  if (isValidated) {
    return (
      <>
        {isSmall && isClient && (
          <HeaderWrapper $isWebView={isWebView}>
            <WhiteHeader
              {...{
                isFixedHeader: isWebView,
                isShowBackButton: true
              }}
            />
          </HeaderWrapper>
        )}
        <ValidatedSection item={item} />
      </>
    );
  }

  const addSchemaOrg = (isPublished || isExpired || isExpiredManually) && item;

  if (currentItemDataLoading) return <CurrentItemPlaceholder />;

  return (
    <>
      {addSchemaOrg && (
        <CurrentItemPageSchema
          {...{ item, isPublished, name: calcModalDescription }}
        />
      )}
      <CurrentItemLayout
        currentItem={item}
        isFavorited={isFavorited}
        setFavorited={setFavorited}
      >
        <CurrentItemSectionWrapper $isDataRefreshing={isDataRefreshing}>
          <CurrentItemSections
            {...{
              data: fullItemData,
              item,
              maskedPhones,
              isEditAvailable,
              isRemoveAvailable,
              isAuthor,
              isResidence,
              isAgency,
              isPublished,
              isLimited,
              isExpired,
              isRejected,
              isPending,
              isLeased,
              isExpiredManually,
              paidServices,
              itemPath,
              prettyPrice,
              prettyArea,
              prettyAreaWithUnits,
              prettyLandAreaWithUnits,
              showElectronicBanners,
              getPriceCurrency,
              onOpenRestoreViewHandler,
              onOpenLimitPaymentViewHandler,
              isFavorited,
              setFavorited,
              userRelatedItemLoading
            }}
          />
        </CurrentItemSectionWrapper>

        <SimilarAdsBlock
          data={fullItemData}
          item={item}
          isAgency={isAgency}
          isResidence={isResidence}
        />
        {shouldLoadAlerts && (
          <CurrentItemAlerts
            item={item}
            isOpenRestoreAlertModal={isOpenRestoreAlertModal}
            isShowForbiddenAlert={isShowForbiddenAlert}
            showReloadPageAlert={showReloadPageAlert}
            onAcceptRestorePaymentHandleClick={
              onAcceptRestorePaymentHandleClick
            }
            onDeclineRestorePayment={onDeclineRestorePayment}
            onCloseForbiddenPopup={handleCloseForbiddenPopup}
          />
        )}
      </CurrentItemLayout>
    </>
  );
};

export const CurrentItemPage = connect(
  null,
  mapDispatchToProps
)(CurrentItemPageComponent);
